import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { PUZZLE_GRID_COLORS } from "../../services/ColorService";
import { cellsForPlacement, placementKey, placementsCovering } from "./tutorCrosswordPlay";

const CELL = 44;
const LINE = `1px solid ${PUZZLE_GRID_COLORS.BORDER_LIGHT}`;

const slotSx = {
    width: CELL,
    height: CELL,
    minWidth: CELL,
    minHeight: CELL,
    boxSizing: "border-box",
    marginRight: "-1px",
    marginBottom: "-1px",
    position: "relative",
    flexShrink: 0,
};

export default function TutorsPlayGrid({
    puzzle,
    selectedKey,
    foundKeys,
    drafts,
    onSelect,
    onType,
    onBackspace,
    onCheck,
}) {
    const boardRef = useRef(null);
    const placements = puzzle?.placements || [];

    useEffect(() => {
        if (selectedKey && boardRef.current) {
            boardRef.current.focus();
        }
    }, [selectedKey]);

    const letterAt = (row, col) => {
        const covering = placementsCovering(placements, row, col);
        for (const placement of covering) {
            if (foundKeys.has(placementKey(placement))) {
                const hit = cellsForPlacement(placement).find((cell) => cell.row === row && cell.col === col);
                return hit?.letter || "";
            }
        }
        if (!selectedKey) {
            return "";
        }
        const selected = placements.find((placement) => placementKey(placement) === selectedKey);
        if (!selected) {
            return "";
        }
        const cells = cellsForPlacement(selected);
        const index = cells.findIndex((cell) => cell.row === row && cell.col === col);
        if (index < 0) {
            return "";
        }
        return (drafts[selectedKey] || "")[index] || "";
    };

    const isSelectedCell = (row, col) => {
        if (!selectedKey) {
            return false;
        }
        const selected = placements.find((placement) => placementKey(placement) === selectedKey);
        if (!selected) {
            return false;
        }
        return cellsForPlacement(selected).some((cell) => cell.row === row && cell.col === col);
    };

    const handleCellClick = (row, col) => {
        const covering = placementsCovering(placements, row, col);
        const open = covering.filter((placement) => !foundKeys.has(placementKey(placement)));
        const choices = open.length > 0 ? open : covering;
        if (choices.length === 0) {
            return;
        }
        if (choices.length === 1) {
            onSelect(choices[0]);
            return;
        }
        const index = choices.findIndex((placement) => placementKey(placement) === selectedKey);
        onSelect(choices[(index + 1) % choices.length]);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Backspace") {
            event.preventDefault();
            onBackspace();
            return;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            onCheck();
            return;
        }
        if (/^[a-zA-Z]$/.test(event.key)) {
            event.preventDefault();
            onType(event.key);
        }
    };

    if (!puzzle?.grid?.length) {
        return null;
    }

    return (
        <Box
            ref={boardRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            sx={{
                display: "inline-flex",
                flexDirection: "column",
                outline: "none",
                maxWidth: "100%",
                p: 1,
                bgcolor: "#fff",
                borderRadius: 1,
                border: `2px solid ${PUZZLE_GRID_COLORS.GRID_OUTER_BORDER}`,
            }}
        >
            {puzzle.grid.map((row, rowIndex) => (
                <Box key={rowIndex} sx={{ display: "flex" }}>
                    {(row || []).map((cell, colIndex) => {
                        if (!cell) {
                            return (
                                <Box
                                    key={colIndex}
                                    sx={{
                                        ...slotSx,
                                        bgcolor: "#bdbdbd",
                                        border: LINE,
                                    }}
                                />
                            );
                        }
                        const selected = isSelectedCell(rowIndex, colIndex);
                        const letter = letterAt(rowIndex, colIndex);
                        return (
                            <Box
                                key={colIndex}
                                role="button"
                                onClick={() => handleCellClick(rowIndex, colIndex)}
                                sx={{
                                    ...slotSx,
                                    bgcolor: selected ? "#E7F6EE" : "#fff",
                                    border: LINE,
                                    cursor: "pointer",
                                    color: "#111",
                                    zIndex: 1,
                                }}
                            >
                                {cell.number ? (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 2,
                                            left: 3,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            color: "#111",
                                        }}
                                    >
                                        {cell.number}
                                    </Box>
                                ) : null}
                                {letter ? (
                                    <Box
                                        sx={{
                                            fontSize: 18,
                                            fontWeight: 700,
                                            textAlign: "center",
                                            lineHeight: `${CELL}px`,
                                            color: "#111",
                                        }}
                                    >
                                        {letter}
                                    </Box>
                                ) : null}
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
}
